import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  composeQueryParams,
  initialDashboardPageMetadata,
  mergeDshbPageMetadataObjects,
  paramsSerializer,
  apiError
} from "../../utils";

export function useCreators() {
  const dispatch = useDispatch();
  const creators = useSelector(state => state.user.creators);
  const platformId = useSelector(state => state.frontend.platformData?.id);
  useEffect(() => {
    if (platformId && creators.length === 0) {
      dispatch(fetchAllCreatorsAsync());
    }
  }, [dispatch, platformId, creators]);
  return creators;
}

export function selectCreatorById(id) {
  return state => state.user.creators.find(u => u.id === Number(id)) || null;
}

export const readAllUsersForDashboardAsync = createAsyncThunk(
  "user/readAllForDashboard",
  async (payload = {}, { getState, signal }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener("abort", () => source.cancel("aborted"));
    const { metadata } = getState().user;
    const params = composeQueryParams(payload, metadata);
    const response = await axios.get("/api/admin/users", {
      params,
      paramsSerializer,
      cancelToken: source.token
    });
    return { users: response.data };
  }
);

export const fetchAllCreatorsAsync = createAsyncThunk(
  "user/fetchAllCreators",
  async (payload, { getState }) => {
    try {
      const platformId = getState().frontend.platformData?.id;
      const response = await axios.get("/api/user/creators", {
        params: { PlatformId: platformId }
      });
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

export const bulkEditUsersAsync = createAsyncThunk(
  "user/bulkEdit",
  async (payload, { dispatch }) => {
    try {
      await axios.post("/api/user/bulk-edit", {
        ids: payload.ids,
        fields: payload.fields
      });
      dispatch(readAllUsersForDashboardAsync());
      return true;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

export const bulkDeleteUsersAsync = createAsyncThunk(
  "user/bulkDelete",
  async (payload, { dispatch }) => {
    try {
      await axios.post("/api/user/bulk-delete", {
        ids: payload
      });
      dispatch(readAllUsersForDashboardAsync());
      return true;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

export const upsertUserAsync = createAsyncThunk(
  "user/upsert",
  async (payload, { rejectWithValue }) => {
    try {
      const body = payload.user;
      if (payload.PlatformId) body.PlatformId = payload.PlatformId;
      const response = await axios.post(`/api/user`, body);
      return { user: response.data };
    } catch (error) {
      return rejectWithValue(apiError(error));
    }
  }
);

export const deleteUserAsync = createAsyncThunk("/user/delete", async payload => {
  await axios.delete(`/api/user/${payload.user.id}`);
  return { id: payload.user.id };
});

export const getUsersCSVAsync = async () => {
  const response = await axios({
    url: "/api/user/export",
    method: "GET",
    responseType: "blob"
  });

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "users.csv");
  document.body.appendChild(link);
  link.click();
};

export const getAdminsAsync = createAsyncThunk("user/getAdmins", async payload => {
  try {
    const response = await axios.get(`/api/user/admins`, {
      params: { PlatformId: payload.PlatformId }
    });
    return { users: response.data };
  } catch (error) {
    console.error("Failed to get admins.", error);
    throw error;
  }
});

export const readAllAccessTagsAsync = createAsyncThunk(
  "accessTags/readAll",
  async (payload = {}) => {
    try {
      const response = await axios.get("/api/access-tags", payload);
      return { accessTags: response.data };
    } catch (error) {
      console.error("Failed to get access tags.", error);
      throw error;
    }
  }
);

export const quickCreateAccessTagAsync = createAsyncThunk(
  "accessTags/quickCreate",
  async (payload, { getState, rejectWithValue, dispatch }) => {
    const { frontend } = getState();
    const platformId = payload.PlatformId || frontend.platformData.id;
    const body = {
      title: payload.title,
      PlatformId: platformId
    };
    try {
      const response = await axios.post(`/api/access-tag`, body);
      await dispatch(readAllAccessTagsAsync());
      return { tag: response.data };
    } catch (error) {
      return rejectWithValue(apiError(error));
    }
  },
  {
    // drop a repeat quick-create of the same tag while it's already in flight (prevents duplicates)
    condition: ({ title }, { getState }) => !getState().user.pendingAccessTagTitles?.includes(title)
  }
);

export const userSlice = createSlice({
  name: "user",
  initialState: {
    loadFailed: false,
    failedLoadArgs: {},
    loading: true,
    pendingAccessTagTitles: [],
    error: null,
    generalError: null,
    users: [],
    admins: [],
    creators: [],
    accessTags: [],
    metadata: initialDashboardPageMetadata
  },
  reducers: {
    clearError: (state, action) => {
      if (state.error) state.error[action.payload.field] = null;
    },
    clearErrors: state => {
      state.error = null;
      state.generalError = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(readAllUsersForDashboardAsync.pending, state => {
        state.loading = true;
        state.loadFailed = false;
      })
      .addCase(readAllUsersForDashboardAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { items, ...metadata } = action.payload.users || {};
        state.users = items || [];
        state.metadata = mergeDshbPageMetadataObjects(state.metadata, metadata);
      })
      .addCase(readAllUsersForDashboardAsync.rejected, (state, action) => {
        state.loading = false;
        if (action.meta.aborted) return;
        state.loadFailed = true;
        state.failedLoadArgs = action.meta.arg || {};
        state.users = [];
      })
      .addCase(fetchAllCreatorsAsync.pending, state => {
        state.loading = true;
      })
      .addCase(fetchAllCreatorsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.creators = action.payload;
      })
      .addCase(fetchAllCreatorsAsync.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(upsertUserAsync.pending, state => {
        state.loading = true;
      })
      .addCase(upsertUserAsync.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(upsertUserAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.fields ?? null;
        state.generalError = state.error ? null : action.payload?.message;
      })
      .addCase(deleteUserAsync.pending, state => {
        state.loading = true;
      })
      .addCase(deleteUserAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.users = state.users.filter(user => user.id !== action.payload.id);
      })
      .addCase(deleteUserAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      })
      .addCase(getAdminsAsync.pending, state => {
        state.loading = true;
      })
      .addCase(getAdminsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.admins = action.payload.users;
      })
      .addCase(getAdminsAsync.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(bulkEditUsersAsync.pending, state => {
        state.loading = true;
      })
      .addCase(bulkEditUsersAsync.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(bulkEditUsersAsync.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(bulkDeleteUsersAsync.pending, state => {
        state.loading = true;
      })
      .addCase(bulkDeleteUsersAsync.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(bulkDeleteUsersAsync.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(readAllAccessTagsAsync.pending, state => {
        state.loading = true;
      })
      .addCase(readAllAccessTagsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.accessTags = action.payload.accessTags;
      })
      .addCase(readAllAccessTagsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      })
      .addCase(quickCreateAccessTagAsync.pending, (state, action) => {
        state.pendingAccessTagTitles = [
          ...(state.pendingAccessTagTitles || []),
          action.meta.arg.title
        ];
      })
      .addCase(quickCreateAccessTagAsync.fulfilled, (state, action) => {
        state.pendingAccessTagTitles = (state.pendingAccessTagTitles || []).filter(
          title => title !== action.meta.arg.title
        );
      })
      .addCase(quickCreateAccessTagAsync.rejected, (state, action) => {
        if (action.meta.condition) return;
        state.pendingAccessTagTitles = (state.pendingAccessTagTitles || []).filter(
          title => title !== action.meta.arg.title
        );
      });
  }
});
export const { clearError, clearErrors } = userSlice.actions;
export default userSlice.reducer;
